import { inject, provide, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import useAuth from '@/composables/auth/useAuth'
import useBaseModal from '@/composables/base/modal/useBaseModal'
import useToast from '@/composables/feedback/useToast'
import useLocalization from '@/composables/localization/useLocalization'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useBaseNavbar = () => {
  const auth = useAuth()
  const route = useRoute()
  const router = useRouter()
  const { localeTexts } = useLocalization()
  const { addToast } = useToast()
  const profileMenuModal = useBaseModal<boolean>(
    Symbol('profile-menu-modal'),
    {
      position: 'top',
      transition: 'top'
    }
  )

  // #region Getters

  const isShowNavbar = computed((): boolean => {
    const isHideMobileNavbar = route.matched.some(record => record.meta.isHideMobileNavbar === true)
    return !isHideMobileNavbar
  })

  // #endregion

  // #region Actions

  const handleLogout = async (): Promise<void> => {
    await auth.logout()
    router.push('/login')
    addToast({
      type: 'common',
      message: localeTexts.value['logout-toast'],
      second: 5
    })
  }

  const handleShowProfileMenuModal = async (): Promise<void> => {
    const { isCanceled, data: isLogout } = await profileMenuModal.reveal()

    if (isCanceled) {
      return
    }

    if (isLogout === true) {
      handleLogout()
    }
  }

  // #endregion

  return {
    profileMenuModal,
    // Getters
    isShowNavbar,
    // Actions
    handleLogout,
    handleShowProfileMenuModal
  }
}

export const defaultKey = Symbol('global:navbar')

export default function useNavbar (): ReturnType<typeof useBaseNavbar> {
  const result = inject<ReturnType<typeof useBaseNavbar> | undefined>(defaultKey, undefined)

  if (result === undefined) {
    const newResult = useBaseNavbar()
    provide(defaultKey, newResult)
    return newResult
  }

  return result
}
